import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import EmergencyAbhaPanel from "../../emergency/components/EmergencyAbhaPanel";
import { registerEmergencyPatient } from "../../emergency/api/emergencyApi";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Other" },
];

const ARRIVAL_MODES = [
  { value: "walk-in", label: "Walk-in" },
  { value: "ambulance", label: "Ambulance" },
];

const EMPTY_PAYER = {
  provider: "",
  policy_number: "",
  tpa: "",
  scheme: "",
  corporate_sponsor: "",
};

const PAYER_FIELDS = [
  ["provider", "Insurance provider", "e.g. Star Health"],
  ["policy_number", "Policy number", "e.g. SH-1234567"],
  ["tpa", "TPA", "e.g. MediAssist"],
  ["scheme", "Scheme", "e.g. CGHS, ESIC"],
  ["corporate_sponsor", "Corporate sponsor", "e.g. Infosys Ltd."],
];

/**
 * Bootstrap reimplementation of the emergency register workflow for the demo
 * RegisterPatient page. Calls the same emergency/visits/ API.
 */
export default function DemoEmergencyRegisterSection() {
  const history = useHistory();

  const [unidentified, setUnidentified] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [identificationMark, setIdentificationMark] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [arrivalMode, setArrivalMode] = useState("walk-in");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [mlcFlag, setMlcFlag] = useState(false);
  const [showPayer, setShowPayer] = useState(false);
  const [payer, setPayer] = useState(EMPTY_PAYER);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [reusedName, setReusedName] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(null);

  const applyAbha = useCallback((m) => {
    if (m.name) {
      setPatientName(m.name);
      setUnidentified(false);
    }
    if (m.gender) setGender(m.gender);
    if (m.age) setAge(m.age);
    if (m.patientId) {
      setSelectedPatientId(m.patientId);
      setReusedName(m.name || "");
    } else {
      setSelectedPatientId(null);
      setReusedName("");
    }
    setErrors({});
  }, []);

  const declineReuse = useCallback(() => {
    setSelectedPatientId(null);
    setReusedName("");
  }, []);

  const clearReuseOnEdit = useCallback(() => {
    setSelectedPatientId((cur) => (cur ? null : cur));
    setReusedName((cur) => (cur ? "" : cur));
  }, []);

  const validate = () => {
    const e = {};
    if (!selectedPatientId && !unidentified && !patientName.trim()) {
      e.patientName = "Enter a name, or mark the patient as unidentified.";
    }
    if (unidentified && !identificationMark.trim()) {
      e.identificationMark =
        "Required when no name is recorded — something staff can recognise this patient by.";
    }
    if (!gender) e.gender = "Select a gender.";
    if (!chiefComplaint.trim()) {
      e.chiefComplaint = "Record why the patient has come, in their own words.";
    }
    if (
      age !== "" &&
      (Number.isNaN(Number(age)) || Number(age) < 0 || Number(age) > 130)
    ) {
      e.age = "Enter an age between 0 and 130.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const visit = await registerEmergencyPatient({
        patientName: unidentified ? undefined : patientName.trim(),
        identificationMark: unidentified
          ? identificationMark.trim()
          : undefined,
        age,
        gender,
        arrivalMode,
        chiefComplaint: chiefComplaint.trim(),
        payer: showPayer ? payer : undefined,
        mlcFlag,
        patientId: selectedPatientId || undefined,
      });
      setRegistered(visit);
      toast.success(
        `${visit.display_name || "Patient"} registered for emergency care`
      );
    } catch (err) {
      toast.error(err?.message || "Could not register the patient");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForAnother = () => {
    setRegistered(null);
    setUnidentified(false);
    setPatientName("");
    setIdentificationMark("");
    setAge("");
    setGender("");
    setArrivalMode("walk-in");
    setChiefComplaint("");
    setMlcFlag(false);
    setShowPayer(false);
    setPayer(EMPTY_PAYER);
    setSelectedPatientId(null);
    setReusedName("");
    setErrors({});
  };

  const payerFields = useMemo(() => PAYER_FIELDS, []);

  if (registered) {
    return (
      <div className="contact-panel mb-50">
        <div className="alert alert-success" role="alert">
          <strong>{registered.display_name}</strong> is registered and ready for
          triage.
        </div>
        <div className="row mb-3">
          {[
            ["Visit number", registered.visit_number],
            ["Hospital identifier", registered.patient_id],
            ["Health-record link", registered.health_record_id],
            ...(registered.temp_label
              ? [["Temporary label", registered.temp_label]]
              : []),
            ...(registered.mlc_case_number
              ? [["Medico-legal case", registered.mlc_case_number]]
              : []),
          ].map(([k, v]) => (
            <div className="col-sm-6 col-md-6 col-lg-6 mb-2" key={k}>
              <div className="form-group mb-0">
                <label className="mb-0">{k}</label>
                <div style={{ fontWeight: 600 }}>{v || "—"}</div>
              </div>
            </div>
          ))}
        </div>
        {registered.duplicate_warning && (
          <div className="alert alert-warning" role="alert">
            {registered.duplicate_warning}
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            className="btn btn__secondary btn__rounded"
            onClick={() => history.push("/emergency_tracking_board")}
          >
            Go to the tracking board
          </button>
          <button
            type="button"
            className="btn btn__secondary btn__rounded"
            onClick={resetForAnother}
          >
            Register another patient
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="contact-panel__form" onSubmit={submit}>
      <div className="row">
        <div className="col-12 mb-3">
          <p className="mb-2" style={{ fontSize: 13.5 }}>
            Registration never blocks care. Payer details, duplicates and the
            rest of the paperwork can all be handled once the patient is being
            treated.
          </p>
        </div>

        <div className="col-12 mb-3">
          <EmergencyAbhaPanel
            onApply={applyAbha}
            taggingPatientId={selectedPatientId || undefined}
            disabled={submitting}
          />
        </div>

        {selectedPatientId && (
          <div className="col-12 mb-3">
            <div className="alert alert-success d-flex justify-content-between align-items-start">
              <div>
                <strong>Attaching to an existing patient</strong>
                <div>
                  {reusedName ? `${reusedName} · ` : ""}
                  {selectedPatientId} — the visit will be linked to this record
                  instead of creating a new one.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn__secondary"
                onClick={declineReuse}
              >
                Register new instead
              </button>
            </div>
          </div>
        )}

        {!selectedPatientId && (
          <div className="col-12 mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="erUnidentified"
                checked={unidentified}
                onChange={(e) => {
                  setUnidentified(e.target.checked);
                  setErrors({});
                }}
                disabled={submitting}
              />
              <label className="form-check-label" htmlFor="erUnidentified">
                Patient cannot be identified (unconscious, or unable to give a
                name)
              </label>
            </div>
          </div>
        )}

        {!unidentified ? (
          <div className="col-sm-6 col-md-6 col-lg-6">
            <div className="form-group">
              <label htmlFor="erPatientName" className="required-label">
                Full name
              </label>
              <input
                id="erPatientName"
                type="text"
                className="form-control"
                placeholder="e.g. Ramesh Iyer"
                value={patientName}
                onChange={(e) => {
                  setPatientName(e.target.value);
                  clearReuseOnEdit();
                }}
                disabled={submitting}
              />
              {errors.patientName && (
                <p className="errorMsg" style={{ color: "red" }}>
                  {errors.patientName}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="col-sm-12 col-md-12 col-lg-12">
            <div className="form-group">
              <label htmlFor="erIdMark" className="required-label">
                Identification mark
              </label>
              <textarea
                id="erIdMark"
                className="form-control"
                rows={2}
                placeholder="Clothing, tattoos, distinguishing features — anything staff can recognise them by"
                value={identificationMark}
                onChange={(e) => setIdentificationMark(e.target.value)}
                disabled={submitting}
              />
              {errors.identificationMark && (
                <p className="errorMsg" style={{ color: "red" }}>
                  {errors.identificationMark}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="col-sm-6 col-md-6 col-lg-6">
          <div className="form-group">
            <label htmlFor="erAge">Age</label>
            <input
              id="erAge"
              type="number"
              min={0}
              max={130}
              className="form-control"
              placeholder="Approximate is fine"
              value={age}
              onChange={(e) => {
                setAge(e.target.value);
                clearReuseOnEdit();
              }}
              disabled={submitting}
            />
            {errors.age && (
              <p className="errorMsg" style={{ color: "red" }}>
                {errors.age}
              </p>
            )}
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-6">
          <div className="form-group">
            <label htmlFor="erGender" className="required-label">
              Gender
            </label>
            <select
              id="erGender"
              className="form-control"
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                clearReuseOnEdit();
              }}
              disabled={submitting}
            >
              <option value="">Select</option>
              {GENDERS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
            {errors.gender && (
              <p className="errorMsg" style={{ color: "red" }}>
                {errors.gender}
              </p>
            )}
          </div>
        </div>

        <div className="col-sm-6 col-md-6 col-lg-6">
          <div className="form-group">
            <label htmlFor="erArrival">Mode of arrival</label>
            <select
              id="erArrival"
              className="form-control"
              value={arrivalMode}
              onChange={(e) => setArrivalMode(e.target.value)}
              disabled={submitting}
            >
              {ARRIVAL_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-sm-12 col-md-12 col-lg-12">
          <div className="form-group">
            <label htmlFor="erComplaint" className="required-label">
              Chief complaint
            </label>
            <textarea
              id="erComplaint"
              className="form-control"
              rows={2}
              placeholder="In the patient's or attendant's own words…"
              value={chiefComplaint}
              onChange={(e) => setChiefComplaint(e.target.value)}
              disabled={submitting}
            />
            {errors.chiefComplaint && (
              <p className="errorMsg" style={{ color: "red" }}>
                {errors.chiefComplaint}
              </p>
            )}
          </div>
        </div>

        <div className="col-12 mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="erMlc"
              checked={mlcFlag}
              onChange={(e) => setMlcFlag(e.target.checked)}
              disabled={submitting}
            />
            <label className="form-check-label" htmlFor="erMlc">
              Flag as a medico-legal case
            </label>
          </div>
        </div>

        <div className="col-12 mb-2">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="erShowPayer"
              checked={showPayer}
              onChange={(e) => setShowPayer(e.target.checked)}
              disabled={submitting}
            />
            <label className="form-check-label" htmlFor="erShowPayer">
              Capture payer details now (optional)
            </label>
          </div>
        </div>

        {showPayer &&
          payerFields.map(([key, label, placeholder]) => (
            <div className="col-sm-6 col-md-6 col-lg-6" key={key}>
              <div className="form-group">
                <label htmlFor={`erPayer_${key}`}>{label}</label>
                <input
                  id={`erPayer_${key}`}
                  type="text"
                  className="form-control"
                  placeholder={placeholder}
                  value={payer[key]}
                  onChange={(e) =>
                    setPayer((p) => ({ ...p, [key]: e.target.value }))
                  }
                  disabled={submitting}
                />
              </div>
            </div>
          ))}

        <div className="col-12 d-flex flex-wrap gap-2 mt-2 mb-4">
          <button
            type="button"
            className="btn btn__secondary btn__rounded"
            onClick={() => history.push("/emergency_tracking_board")}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn__secondary btn__rounded"
            disabled={submitting}
          >
            {submitting ? "Registering…" : "Register patient"}
          </button>
        </div>
      </div>
    </form>
  );
}
